import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Borrower } from '../data/borrowers';
import { QueueRow } from './QueueRow';
interface QueueTableProps {
  borrowers: Borrower[];
  onRowClick: (borrower: Borrower) => void;
  onStatusChange: (borrowerId: string, newStatus: 'Attempted' | 'Connected' | 'Converted') => void;
  onCreateLoan: (borrowerId: string) => void;
  onMarkComplete: (borrowerId: string) => void;
}
type SortKey = 'name' | 'loanNumber' | 'fundedDate' | 'loanType' | 'interestRate' | 'loanAmount' | 'loa' | 'manager' | 'status' | 'lastActivity';
type SortDirection = 'asc' | 'desc';
const COLUMNS: {
  key: SortKey | null;
  label: string;
  width: string;
}[] = [{
  key: 'name',
  label: 'Borrower Name',
  width: 'w-[220px]'
}, {
  key: 'loanNumber',
  label: 'Loan Number',
  width: 'w-[140px]'
}, {
  key: 'fundedDate',
  label: 'Funded Date',
  width: 'w-[130px]'
}, {
  key: 'loanType',
  label: 'Loan Type',
  width: 'w-[120px]'
}, {
  key: 'interestRate',
  label: 'Interest Rate',
  width: 'w-[110px]'
}, {
  key: 'loanAmount',
  label: 'Loan Amount',
  width: 'w-[130px]'
}, {
  key: 'loa',
  label: 'LOA',
  width: 'w-[140px]'
}, {
  key: 'manager',
  label: 'Manager',
  width: 'w-[140px]'
}, {
  key: 'status',
  label: 'Current Status',
  width: 'w-[120px]'
}, {
  key: 'lastActivity',
  label: 'Last Activity',
  width: 'w-[110px]'
}, {
  key: null,
  label: 'Actions',
  width: 'w-[260px]'
}];
const STATUS_ORDER: Record<string, number> = {
  New: 0,
  Attempted: 1,
  Connected: 2,
  Converted: 3
};
export function QueueTable({
  borrowers,
  onRowClick,
  onStatusChange,
  onCreateLoan,
  onMarkComplete
}: QueueTableProps) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };
  const sortedBorrowers = useMemo(() => {
    if (!sortKey) return borrowers;
    const sorted = [...borrowers].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortKey === 'status') {
        av = STATUS_ORDER[a.status] ?? 99;
        bv = STATUS_ORDER[b.status] ?? 99;
      } else if (sortKey === 'interestRate' || sortKey === 'loanAmount') {
        av = a[sortKey];
        bv = b[sortKey];
      } else {
        av = String(a[sortKey]).toLowerCase();
        bv = String(b[sortKey]).toLowerCase();
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [borrowers, sortKey, sortDirection]);
  return <div className="flex-1 overflow-auto" data-id="element-167">
      <table className="w-full table-fixed min-w-[1600px]" data-id="element-168">
        <colgroup data-id="element-169">
          {COLUMNS.map((col, i) => <col key={i} className={col.width} data-id="element-170" />)}
        </colgroup>
        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10" data-id="element-171">
          <tr data-id="element-172">
            {COLUMNS.map(col => {
            const isActive = col.key && sortKey === col.key;
            const isSortable = col.key !== null;
            return <th key={col.label} className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap" data-id="element-173">
                  {isSortable ? <button type="button" onClick={() => handleSort(col.key as SortKey)} className={`flex items-center gap-1.5 uppercase tracking-wider hover:text-slate-900 transition-colors ${isActive ? 'text-brand-orange-600' : 'text-slate-700'}`} data-id="element-174">
                      {col.label}
                      {isActive ? sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5" data-id="element-175" /> : <ChevronDown className="w-3.5 h-3.5" data-id="element-176" /> : <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400" data-id="element-177" />}
                    </button> : col.label}
                </th>;
          })}
          </tr>
        </thead>
        <tbody className="bg-white" data-id="element-178">
          {sortedBorrowers.map(borrower => <QueueRow key={borrower.id} borrower={borrower} onClick={() => onRowClick(borrower)} onStatusChange={onStatusChange} onCreateLoan={onCreateLoan} onMarkComplete={onMarkComplete} data-id="element-179" />)}
        </tbody>
      </table>
    </div>;
}